import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Alert,
  LinearProgress,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Chip
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Preview as PreviewIcon,
  ContactMail as LeadIcon
} from '@mui/icons-material';
import logger from '../../utils/logger';

const LeadImportDialog = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [importResults, setImportResults] = useState(null);
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);

  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

  const reset = () => {
    setError('');
    setSuccess('');
  };

  const handleFileChange = (event) => {
    const selected = event.target.files?.[0];
    reset();
    setPreviewData(null);
    setImportResults(null);

    if (!selected) {
      setFile(null);
      return;
    }

    if (!selected.name.toLowerCase().endsWith('.csv')) {
      setError('Please choose a .csv file');
      setFile(null);
      return;
    }

    setFile(selected);
    logger.log('[LeadImportDialog] File selected:', selected.name, selected.size);
  };

  /**
   * Both preview and import hit the same endpoint; `preview` decides whether
   * anything is written.
   */
  const submit = async ({ preview }) => {
    if (!file) {
      setError('Please choose a CSV file first');
      return;
    }

    reset();
    if (preview) {
      setPreviewLoading(true);
      setPreviewData(null);
    } else {
      setLoading(true);
      setImportResults(null);
      setPreviewData(null);
    }

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (preview) formData.append('preview', 'true');

      const response = await fetch(`${apiBase}/api/admin/leads/import`, {
        method: 'POST',
        headers: {
          // Content-Type is intentionally omitted — the browser must set the
          // multipart boundary itself.
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Request failed');

      if (preview) {
        setPreviewData(data);
      } else {
        setImportResults(data.stats);
        setSuccess(data.message || 'Import completed');
      }
      logger.log(`[LeadImportDialog] ${preview ? 'Preview' : 'Import'} completed:`, data.stats);
    } catch (err) {
      setError(`${preview ? 'Preview' : 'Import'} failed: ${err.message}`);
    } finally {
      setPreviewLoading(false);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setPreviewData(null);
    setImportResults(null);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    reset();
    onClose();
  };

  const busy = loading || previewLoading;
  const stats = previewData?.stats;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <LeadIcon color="primary" />
          Import Leads from CSV
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="info">
              <Typography variant="body2" gutterBottom>
                <strong>Outbound B2B lead import</strong>
              </Typography>
              <Typography variant="body2" component="div">
                • Requires a <code>property_management_company_name</code> column;{' '}
                <code>city</code>, <code>phone</code>, <code>email</code>, <code>website</code>,{' '}
                <code>street</code>, <code>state</code> and <code>zip</code> are optional
                <br />
                • Creates <strong>leads</strong> only — no customer or user accounts
                <br />
                • Rows are deduplicated by company name; re-importing updates instead of duplicating
                <br />
                • Outreach fields (status, notes, last contacted) are never overwritten by an import
                <br />
                • Companies with neither an email nor a phone are skipped
              </Typography>
            </Alert>
          </Grid>

          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Choose a file
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Preview first to check the parsed counts, then import. Max 5MB.
              </Typography>

              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={5}>
                  <Button
                    variant="outlined"
                    component="label"
                    fullWidth
                    disabled={busy}
                    startIcon={<UploadIcon />}
                  >
                    {file ? file.name : 'Select CSV'}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv,text/csv"
                      hidden
                      onChange={handleFileChange}
                    />
                  </Button>
                </Grid>
                <Grid item xs={12} sm={7}>
                  <Box display="flex" gap={1}>
                    <Button
                      variant="outlined"
                      startIcon={<PreviewIcon />}
                      onClick={() => submit({ preview: true })}
                      disabled={busy || !file}
                      fullWidth
                    >
                      Preview
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<UploadIcon />}
                      onClick={() => submit({ preview: false })}
                      disabled={busy || !file}
                      fullWidth
                    >
                      Import
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {busy && (
            <Grid item xs={12}>
              <LinearProgress />
              <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                {loading ? 'Importing leads...' : 'Parsing preview...'}
              </Typography>
            </Grid>
          )}

          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
          {success && (
            <Grid item xs={12}>
              <Alert severity="success">{success}</Alert>
            </Grid>
          )}

          {previewData && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2} flexWrap="wrap">
                    <Typography variant="h6">Preview</Typography>
                    <Chip label={`${stats?.deduped ?? 0} unique companies`} color="success" size="small" />
                    <Chip label={`${stats?.withEmail ?? 0} with an email`} color="primary" size="small" />
                    {stats?.duplicatesCollapsed > 0 && (
                      <Chip
                        label={`${stats.duplicatesCollapsed} duplicates collapsed`}
                        size="small"
                      />
                    )}
                    {stats?.skipped > 0 && (
                      <Chip label={`${stats.skipped} skipped`} color="warning" size="small" />
                    )}
                  </Box>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {stats?.totalParsed ?? 0} rows parsed. Nothing has been saved yet.
                  </Typography>

                  {stats?.sample?.length > 0 && (
                    <>
                      <Divider sx={{ mb: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        First {stats.sample.length} rows
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Company</TableCell>
                              <TableCell>City</TableCell>
                              <TableCell>Phone</TableCell>
                              <TableCell>Email</TableCell>
                              <TableCell>Address</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {stats.sample.map((lead, index) => (
                              <TableRow key={`${lead.companyName}-${index}`}>
                                <TableCell>{lead.companyName}</TableCell>
                                <TableCell>{lead.city || '—'}</TableCell>
                                <TableCell>{lead.phone || '—'}</TableCell>
                                <TableCell>{lead.email || '—'}</TableCell>
                                <TableCell>
                                  {lead.street
                                    ? `${lead.street}, ${lead.city || ''} ${lead.state || ''} ${lead.zip || ''}`.trim()
                                    : '—'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}

          {importResults && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Import Results
                  </Typography>
                  <Grid container spacing={2}>
                    {[
                      { label: 'Rows parsed', value: importResults.totalParsed },
                      { label: 'Created', value: importResults.created },
                      { label: 'Updated', value: importResults.updated },
                      { label: 'Skipped', value: importResults.skipped },
                      { label: 'Errors', value: importResults.errors }
                    ].map(({ label, value }) => (
                      <Grid item xs={6} sm={4} md={2.4} key={label}>
                        <Typography variant="h5">{value ?? 0}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {label}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>

                  {importResults.details?.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Skipped and failed rows
                      </Typography>
                      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Company</TableCell>
                              <TableCell>Outcome</TableCell>
                              <TableCell>Reason</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {importResults.details.map((detail, index) => (
                              <TableRow key={`${detail.company}-${index}`}>
                                <TableCell>{detail.company}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={detail.status}
                                    size="small"
                                    color={detail.status === 'error' ? 'error' : 'default'}
                                  />
                                </TableCell>
                                <TableCell>{detail.message}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LeadImportDialog;
